import { dayOf, isRecord, ms, wall, type Doc } from '@day';
import { deadlineOn, FRESH_MS, GOOGLE_MS } from './waiting';

/**
 * "Copy to a new route" (board `swap-copy`): a route's stops made ready for a new one, with nothing of what became of
 * them. Kept apart from the actions so it can be tested.
 */

/**
 * A stop's position when it's the customer's own, not Google's: with no place ID it never came from Google
 * (route_export.dart), unless Waiting says it did (a stop found on the map as it went onto its route, before those kept
 * their place IDs). Null once the position has gone (Google's 30 days take a pin's too).
 */
export function pinOf(rec: Doc, raw: Doc): { lat: number; lng: number } | null {
  if (typeof rec.lat !== 'number' || typeof rec.lng !== 'number' || rec.placeId) return null;
  const from = isRecord(raw.fromWaiting) ? raw.fromWaiting[String(rec.id)] : null;
  if (isRecord(from) && from.pin === false) return null;
  return { lat: rec.lat, lng: rec.lng };
}

/** A handover's two stops are where two couriers met for one parcel (`Stop.handover`): nothing to send again. */
const handover = (id: string) => id.endsWith('-collect') || id.endsWith('-meet');

export interface Copied {
  /** As a new route stores it, under a new id; its position only if it goes on as it is. */
  stored: Doc;
  pin: boolean;
  /** Looked up again first: Google gave its position more than a day ago, or it has none any more. */
  again: boolean;
}

/**
 * [stops] as a new route's, each under a new id, a delivery still naming its pickup. A deadline from an earlier day
 * moves to the same time today. [until]: when Google's positions on the route must go, which says how old they are.
 */
export function copiedStops(stops: Doc[], raw: Doc, o: { until: number | null; now: number; today: string; zone: string; newId: () => string }): Copied[] {
  const kept = stops.filter((s) => !handover(String(s.id)));
  const ids = new Map(kept.map((s) => [String(s.id), o.newId()]));
  const fresh = o.until != null && o.until - o.now > GOOGLE_MS - FRESH_MS;
  return kept.map((s) => {
    const pin = !!pinOf(s, raw);
    const again = !pin && !(fresh && typeof s.lat === 'number' && typeof s.lng === 'number');
    const { lat, lng, ...rest } = s;
    const by = ms(s.deliverBy, o.zone);
    const late = by != null && dayOf(by, o.zone) < o.today;
    const w = by != null ? wall(by, o.zone) : null;
    return {
      pin,
      again,
      stored: {
        ...rest,
        ...(again ? {} : { lat, lng }),
        id: ids.get(String(s.id))!,
        pickupId: ids.get(String(s.pickupId)) ?? null,
        retryOf: null,
        sentStopId: null,
        ...(late ? { deliverBy: deadlineOn(o.today, `${String(w!.hour).padStart(2, '0')}:${String(w!.minute).padStart(2, '0')}`) } : {}),
      },
    };
  });
}

/**
 * A stored stop with the position found again: its place ID kept with it, so nothing takes Google's position for the
 * customer's own, and the address the business wrote kept as written, since a stop with a place ID is otherwise taken
 * to be in Google's wording.
 */
export function placedAgain(s: Doc, place: { lat: number; lng: number; placeId?: string; precision: 'exact' | 'low' }): Doc {
  return {
    ...s,
    lat: place.lat,
    lng: place.lng,
    placeId: s.placeId ?? place.placeId ?? null,
    writtenAddress: s.writtenAddress ?? (s.placeId ? null : s.address),
    precision: place.precision === 'low' ? 'low' : (s.precision ?? 'exact'),
  };
}
